<template>
  <div
    class="relative flex min-h-[640px] flex-col rounded-xl bg-white px-3 pb-9 pt-8 shadow-pm"
  >
    <h3
      class="mb-2 flex w-full justify-start p-2 text-base font-semibold leading-5"
      data-testid="chart-title"
    >
      {{ titlePrices }}
    </h3>
    <div
      v-if="loadingPricesPortal"
      class="flex h-full w-full grow items-center justify-center"
    >
      <TheLoader />
    </div>
    <div v-else class="flex flex-col gap-6">
      <section class="flex w-full flex-col">
        <SimpleLineChart
          :key="updatedPricesPortal"
          css-classes="h-[311px] w-full"
          :chart-data="dataCollection"
          :y-axis-label="axisLabel"
          :is-mobile="isMobile"
          :plugins="plugins"
        />
      </section>
      <section class="relative w-full">
        <div class="h-full w-full" data-testid="portal-summary-table">
          <div
            class="flex justify-between rounded-lg bg-s-300 px-3 py-2 pr-[60px] text-sm font-semibold leading-[17px] md:pr-[75px]"
            data-testid="portal-summary-table-header"
          >
            <div> {{ titlePricesSummary }} </div>
            <div>{{ selectedLabel }}</div>
          </div>
          <div data-testid="portal-summary-table-content">
            <div
              v-for="(prices, i) in pricesSummaryPortal"
              :key="`${prices.label}-${i}`"
            >
              <div
                class="flex border-b-2 border-p-400 p-3 pb-[6px] font-semibold"
                :style="`color: ${getClassesColor(prices.classTextColor)}`"
              >
                {{ prices.label }}
                <IconPriceHighlightedLogo
                  v-if="prices.classTextColor === 'industry-leading'"
                  class="ml-2"
                />
              </div>
              <div class="pl-3 md:px-3">
                <PriceAssessmentsPortalPriceTableRow
                  v-for="price in prices.values"
                  :key="price.name"
                  :style="`color: ${getClassesColor(price.classTextColor)}`"
                  :price="price"
                  :parent-price-name="prices.label"
                  :selected-label="selectedLabel"
                  :selected-filter="filters[selectedLabel]"
                  :slug="slug"
                  :has-marketing-page="hasMarketingPage"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useScreenSize } from '@/composables/useScreenSize'
import CrosshairPlugin from 'chartjs-plugin-crosshair'
import { computed, onMounted, ref, watch } from 'vue'
import IconPriceHighlightedLogo from '~/assets/icons/price-highlighted-logo.svg'
import TheLoader from '~/components/TheLoader.vue'
import { getClassesColor } from '~/utils/get-classes-color'
import SimpleLineChart from './SimpleLineChart.vue'

const props = withDefaults(
  defineProps<{
    slug: string
    category: string
    titlePrices?: string | null
    pricesPortal: Array<any>
    loadingPricesPortal?: boolean
    titlePricesSummary?: string | null
    pricesSummaryPortal?: Array<any> | null
    hasMarketingPage?: boolean
  }>(),
  {
    titlePrices: null,
    loadingPricesPortal: true,
    titlePricesSummary: null,
    pricesSummaryPortal: null,
    hasMarketingPage: false,
  },
)

const updatedPricesPortal = ref(0)
const colors = [
  '#EA8000',
  '#FDC723',
  '#FEEBB2',
  '#92A678',
  '#374C33',
  '#7D2F59',
  '#BA86A2',
  '#F2A900',
  '#589F97',
]
const dataCollection = ref({
  labels: [],
  datasets: [],
})
const plugins = [CrosshairPlugin]
const selectedLabel = ref('Current')
const filters = {
  Current: 'today',
}
const { isMobile } = useScreenSize()

const pricesData = computed(() => {
  return props.pricesPortal ?? []
})

const axisLabel = computed(() => {
  if (pricesData.value?.[0]?.currency.symbol !== '') {
    return `${pricesData.value?.[0]?.currency.iso}/${pricesData.value?.[0]?.unitOfMeasure.name}`
  }
  return 'Index Value'
})

watch(
  pricesData,
  () => {
    extractDatesByPricesData()
    setDatasetsByPricesData()
    updatedPricesPortal.value++
  },
  { deep: true },
)

const extractDatesByPricesData = () => {
  const dates = new Set<string>()
  pricesData.value?.forEach((item) => {
    item.series.forEach((serie) => {
      dates.add(serie.date)
    })
  })
  dataCollection.value.labels = [...dates].sort(
    (a, b) => +new Date(a) - +new Date(b),
  )
}

const setDatasetsByPricesData = () => {
  dataCollection.value.datasets = pricesData.value?.map((data, index) => {
    return {
      label: data.category.name,
      fill: false,
      yAxisID: 'y',
      digits: 0,
      unit: data.currency.symbol ?? '',
      spanGaps: true,
      lineTension: 0,
      borderColor: colors[index],
      backgroundColor: colors[index],
      data: data.series.map((serie) => ({
        x: serie.date,
        y: serie.valueMid,
      })),
    }
  })
}

onMounted(() => {
  extractDatesByPricesData()
  setDatasetsByPricesData()
})
</script>
